"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { LuInfo } from "react-icons/lu";
import { BiLoaderAlt } from "react-icons/bi";

import { cn } from "~/utils/fontsConfig";
import { generateRandomName } from "~/utils/constants";
import { CreateEventIcon } from "~/app/_components/Icons";

import Modal from "~/components/v3/themed/Modal";
import { Button, ButtonProps } from "~/components/v4/themed/Button";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/shadcn/Select";
import { Label } from "~/components/v3/shadcn/Label";

import {
    CommunitiesToHostOption,
    followCommunity,
    getUserFollowersCount,
} from "../../events/create/actions";
import { CreateEventFormSchema } from "../../events/create/utils";
import CreateEventWithAIModal from "../../events/_components/modals/CreateEventWithAIModal";
import { FaWandMagicSparkles } from "react-icons/fa6";
import { Role } from "@prisma/client";
import { If } from "~/components/v3/Conditional";
import { useSession } from "next-auth/react";

export const CreateEventButton = ({
    variant,
    user,
    communities,
    type = "default",
    size = "default",
    className,
    communityIdOverride,
    Icon,
    communitySlug,
}: {
    variant?: string;
    user: {
        id: string;
        name?: string | null;
        image?: string | null;
    };
    communities?: CommunitiesToHostOption[];
    type?: "default" | "compact" | "link";
    size?: ButtonProps["size"];
    Icon?: React.ReactNode;
    className?: React.HTMLAttributes<HTMLButtonElement>["className"];
    communityIdOverride?: string | null;
    communitySlug?: string | null;
}) => {
    const router = useRouter();

    // TODO: Undo
    const [isOpen, setIsOpen] = useState(false);
    // const [isOpen, setIsOpen] = useState(true);
    const [isPending, startTransition] = useTransition();

    const selectedCommunity = communitySlug
        ? communities?.find((community) => community.slug === communitySlug)
        : null;

    const handleOpen = async () => {
        if (communityIdOverride || selectedCommunity) {
            const searchParams = new URLSearchParams({
                type: "community",
                communityId: (selectedCommunity?.id ??
                    communityIdOverride) as string,
            });
            return router.push(
                `/beta/events/create?${searchParams.toString()}`,
            );
        }

        if (communitySlug && !selectedCommunity) {
            handleFollowCommunity(communitySlug as string);
        } else {
            setIsOpen(true);
        }
    };

    const handleFollowCommunity = (id: string) => {
        try {
            startTransition(async () => {
                const communityId = await followCommunity(
                    communitySlug as string,
                );

                const searchParams = new URLSearchParams({
                    type: "community",
                    communityId: communityId,
                });

                router.push(`/beta/events/create?${searchParams.toString()}`);
            });
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            {type === "default" ? (
                <Button
                    variant={!!variant ? (variant as any) : "default"}
                    onClick={handleOpen}
                    size={size}
                    disabled={isPending}
                    className={cn(className, "h-[30px]")}
                >
                    {Icon && <span className="mr-2">{Icon}</span>}
                    {isPending ? "Loading..." : "Create event"}
                </Button>
            ) : type === "compact" ? (
                <button
                    className="flex h-[24px] w-[64px] flex-1 items-center justify-center"
                    onClick={handleOpen}
                    disabled={isPending}
                >
                    {isPending ? (
                        <BiLoaderAlt className="h-6 w-6 animate-spin text-v3-primary" />
                    ) : (
                        <CreateEventIcon />
                    )}
                </button>
            ) : (
                <button
                    className="flex items-center gap-2 text-v3-primary"
                    onClick={handleOpen}
                    disabled={isPending}
                >
                    <PlusIcon />
                    {isPending ? "Loading..." : "Create event"}
                </button>
            )}

            <EventSelectCommunityModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                user={user}
                communities={communities ?? []}
            />
        </>
    );
};

const EventSelectCommunityModal = ({
    isOpen,
    setIsOpen,
    user,
    communities,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    user: {
        id: string;
        name?: string | null;
        image?: string | null;
    };
    communities: CommunitiesToHostOption[];
}) => {
    const { data: session } = useSession();
    const router = useRouter();
    // TODO: Undo
    const [showCreateWithAIModal, setShowCreateWithAIModal] = useState(false);
    // const [showCreateWithAIModal, setShowCreateWithAIModal] = useState(true);
    const [selectedHost, setSelectedHost] = useState<
        CreateEventFormSchema["hostId"]
    >({
        type: "user",
        userId: user.id,
    });

    const communitiesRecord = communities.reduce(
        (acc, community) => {
            acc[community.id] = community;
            return acc;
        },
        {} as Record<string, CommunitiesToHostOption>,
    );

    const { data: followersCount } = useQuery({
        queryKey: ["userFollowersCount", user.id],
        queryFn: () => getUserFollowersCount(user.id),
    });

    const handleHostSelect = (type: "user" | "community", hostId: string) => {
        setSelectedHost(
            type === "user"
                ? { type, userId: hostId }
                : { type, communityId: hostId },
        );
    };

    const selectedHostCommunity =
        selectedHost.type === "community"
            ? communitiesRecord[selectedHost.communityId]
            : null;

    const handleContinue = () => {
        const params = new URLSearchParams(selectedHost);
        router.push(`/beta/events/create?${params.toString()}`);
        setIsOpen(false);
    };

    if (showCreateWithAIModal) {
        return (
            <CreateEventWithAIModal
                open={showCreateWithAIModal}
                onOpenChange={setShowCreateWithAIModal}
            />
        );
    }

    return (
        <Modal
            title="Choose Your Calendar"
            open={isOpen}
            onOpenChange={setIsOpen}
            className="!max-w-[400px]"
            footer={
                <div className="flex w-full justify-end gap-3 border-t border-v3-N-400/50 pt-4">
                    <Button
                        variant="secondary"
                        onClick={() => setIsOpen(false)}
                    >
                        Nevermind
                    </Button>

                    <Button onClick={handleContinue}>Continue</Button>

                    <If condition={session?.user?.role === Role.ADMIN}>
                        <Button
                            onClick={() => setShowCreateWithAIModal(true)}
                            className="bg-v4-PA202"
                        >
                            <FaWandMagicSparkles className="h-4 w-4 text-white" />
                            Create with AI
                        </Button>
                    </If>
                </div>
            }
        >
            <div className="flex flex-col gap-6">
                <p className="text-lg font-[300] text-v3-N-300">
                    Create an event in your personal calendar or in a community
                    you belong to.
                </p>

                <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                        <Label className="text-sm font-[300] text-v3-N-300">
                            Personal account
                        </Label>

                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => handleHostSelect("user", user.id)}
                            className={cn(
                                "grid w-full grid-cols-[auto_auto_1fr] items-center gap-3 rounded-[10px] bg-v3-N-500 p-2 transition-colors hover:bg-accent",
                                selectedHost.type === "user" &&
                                    "border border-v3-clover",
                            )}
                        >
                            <input
                                type="radio"
                                checked={selectedHost.type === "user"}
                                className="h-4 w-4 border-v3-N-400 bg-slate-500  bg-transparent text-v3-clover checked:border-v3-clover checked:bg-v3-clover focus:ring-0 focus:ring-offset-0"
                                readOnly
                            />
                            <UserAvatar
                                user={user}
                                className="min-h-6 min-w-6"
                            />
                            <div className="flex flex-col gap-1">
                                <span className="font-[500]">
                                    {user.name ?? generateRandomName(user.id)}
                                </span>

                                {!!followersCount && (
                                    <span className="text-xs text-v3-N-300">
                                        You have {followersCount} follower(s)
                                        you can invite to your personal event.
                                    </span>
                                )}
                            </div>
                        </div>

                        <span className="text-xs font-[300] leading-5 tracking-wide text-v3-N-300">
                            Events created from your personal account will
                            appear as hosted by you.
                        </span>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label className="text-sm font-[300] text-v3-N-300">
                            Community account{" "}
                            <span className="text-xs font-[300] text-v3-N-300">
                                (optional)
                            </span>
                        </Label>

                        <Select
                            value={selectedHost.type}
                            onValueChange={(value) => {
                                if (value === "no-community") {
                                    handleHostSelect("user", user.id);
                                } else {
                                    handleHostSelect("community", value);
                                }
                            }}
                            disabled={communities.length === 0}
                        >
                            <SelectTrigger
                                className={cn(
                                    "h-auto w-full rounded-[10px]",
                                    selectedHost.type === "community" &&
                                        "border border-v3-clover",
                                )}
                            >
                                <SelectValue placeholder="Select a community">
                                    {selectedHostCommunity ? (
                                        <div
                                            className={cn(
                                                "grid grid-cols-[auto_auto_1fr] items-center gap-3 ",
                                            )}
                                        >
                                            <input
                                                type="radio"
                                                checked={
                                                    !!selectedHostCommunity
                                                }
                                                className="border-input h-4 w-4 rounded-full border text-v3-clover focus:ring-v3-clover focus:ring-offset-0"
                                                readOnly
                                            />
                                            <CommunityAvatar
                                                community={
                                                    selectedHostCommunity
                                                }
                                                className="min-h-6 min-w-6"
                                            />
                                            <div className="flex flex-col gap-1 text-left">
                                                <span className="text-base font-[500]">
                                                    {selectedHostCommunity.name}
                                                </span>

                                                {selectedHostCommunity.nearbyMembersCount >
                                                    0 && (
                                                    <span className="text-xs text-v3-N-300">
                                                        This community has
                                                        followers nearby that
                                                        you can invite to your
                                                        event.
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3 text-v3-N-300">
                                            <span>Select community</span>
                                        </div>
                                    )}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent
                                className="rounded-[10px]"
                                side="bottom"
                            >
                                <SelectItem value="no-community">
                                    <div className="flex items-center gap-3">
                                        Deselect community
                                    </div>
                                </SelectItem>

                                {communities.map((community) => (
                                    <SelectItem
                                        key={community.id}
                                        value={community.id}
                                        className="relative"
                                    >
                                        <div className="flex items-center gap-3">
                                            <CommunityAvatar
                                                community={community}
                                                className="h-6 w-6"
                                            />
                                            <span>{community.name}</span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {communities.length === 0 && (
                            <span className="flex items-center text-xs font-[300] leading-5 text-v3-N-200">
                                <LuInfo className="mr-1 h-4 w-4" />
                                Follow a community to propose events in it.
                            </span>
                        )}
                        <span className="text-xs font-[300] leading-5 text-v3-N-300">
                            Events created from a community account will be
                            linked to that community and visible to its members.
                        </span>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

const PlusIcon = () => {
    return (
        <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M5.25 5.25V1.5C5.25 1.08579 5.58579 0.75 6 0.75C6.41421 0.75 6.75 1.08579 6.75 1.5V5.25H10.5C10.9142 5.25 11.25 5.58579 11.25 6C11.25 6.41421 10.9142 6.75 10.5 6.75H6.75V10.5C6.75 10.9142 6.41421 11.25 6 11.25C5.58579 11.25 5.25 10.9142 5.25 10.5V6.75H1.5C1.08579 6.75 0.75 6.41421 0.75 6C0.75 5.58579 1.08579 5.25 1.5 5.25H5.25Z"
                fill="#E2FF05"
            />
        </svg>
    );
};
